import { useCallback, useRef, useState } from 'react';
import getImages from '../../../../utils/GetImages';
import { imageUrl as imageURLMaker } from '../../../../utils/staticUrls';
import {
  IMAGE_GALLERY,
  getCFDefaultImage,
  getMediaData,
  isShopTheProjectAmbientImage,
  isShopTheProjectAmbientImageCategory,
  isVideo,
} from '../../ImageGalleryHelper';

/**
 * Custom hook that encapsulates all business logic for ImageGalleryHeadline
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useImageGalleryHeadline = ({
  zoomSize,
  activeImageIndex,
  images,
  imagesPerSlide,
  activeIndicatorIndex,
  setActiveIndicatorIndex,
  setActiveImageIndex,
  imageQuality,
  headlineImageHeight,
  headlineImageWidth,
  crop,
  setVideoPlayingForSoftline,
  productName,
  brandName,
  product,
  appCtx,
}) => {
  // State management
  const [zoomLevel, setZoomLevel] = useState();
  const [isVideoPlaying, setVideoPlaying] = useState();
  const [isCoachmarkVisible, setIsCoachmarkVisible] = useState(false);

  const headlineRef = useRef();

  const { siteConfig, regionCode, store = '' } = appCtx || {};
  const { toggles, configurations, textDictionary } = siteConfig || {};
  const { isCFMediasEnabled = false, isShopTheProjectAmbientImageEnabled = false } = toggles || {};
  const { mediaDomains = [] } = configurations || {};
  const { shopTheProjectConfig } = configurations || {};
  const { gCategories: stpAmbientImagesCategories = [] } = shopTheProjectConfig || {};
  const { FALLBACK_IMAGES_DOMAIN: fallBackImagesDomain = '' } = textDictionary || {};

  const { variants, attributes } = product || {};
  const { GSCCategoryId: gCategoryId = '' } = attributes || {};
  const firstVariant = variants?.[0];
  const firstOffering = firstVariant?.offerings?.[0];
  const sellerName = (firstOffering?.sellerName || '').toLowerCase();

  // Error handling for image fallback
  const onErrorFallback = useCallback(
    ({ currentTarget }) => {
      /* eslint-disable no-param-reassign */
      currentTarget.onerror = null;
      currentTarget.src = isCFMediasEnabled
        ? getCFDefaultImage({ sellerName, regionCode, fallBackImagesDomain })
        : imageURLMaker('defaultImage.jpg');
    },
    [isCFMediasEnabled, sellerName, regionCode, fallBackImagesDomain]
  );

  // Shop the project ambient image validation
  const isShopTheProjectEnabledWithAmbientImage = useCallback(
    (imageMediaUrl) => {
      return (
        isShopTheProjectAmbientImageEnabled &&
        isShopTheProjectAmbientImage(imageMediaUrl) &&
        isShopTheProjectAmbientImageCategory(gCategoryId, stpAmbientImagesCategories)
      );
    },
    [isShopTheProjectAmbientImageEnabled, gCategoryId, stpAmbientImagesCategories]
  );

  // Mouse event handlers
  const handleMouseLeave = useCallback(() => {
    setZoomLevel();
  }, []);

  const handleMouseOver = useCallback(
    (e) => {
      // Early return if zoomSize is 0 or falsy
      if (!zoomSize || zoomSize === 0 || (zoomSize.x === 0 && zoomSize.y === 0)) {
        return;
      }

      if (!headlineRef.current) {
        return;
      }

      const { pageX, pageY } = e;
      const {
        width: clientWidth,
        height: clientHeight,
        left: offsetLeft,
        top: offsetTop,
      } = headlineRef.current.getBoundingClientRect();
      const { scrollTop } = document.documentElement;

      // Use provided zoom size
      const effectiveZoomSize = {
        x: zoomSize.x || clientWidth * 2,
        y: zoomSize.y || clientHeight * 2,
      };

      if (!effectiveZoomSize.x || !clientWidth || !clientHeight) {
        return;
      }

      const zoomRatio = {
        x: (clientWidth - effectiveZoomSize.x) * ((pageX - offsetLeft) / clientWidth),
        y: (clientHeight - effectiveZoomSize.y) * ((pageY - scrollTop - offsetTop) / clientHeight),
      };

      setZoomLevel(zoomRatio);
    },
    [zoomSize]
  );

  const handleMouseOverButton = useCallback((e) => {
    e.stopPropagation();
    setZoomLevel();
  }, []);

  // Navigation handlers
  const onNextHeadline = useCallback(() => {
    setVideoPlaying(false);
    setVideoPlayingForSoftline(false);
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex + 1;

    const isLastImage = activeImageIndex + 1 === images.length;
    const isLastImageOfSlide = (activeImageIndex + 1) % imagesPerSlide === 0;

    if (isLastImage) {
      newActiveIndicatorIndex = 0;
      newActiveImageIndex = 0;
    } else if (!isLastImage && isLastImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex + 1;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [
    activeImageIndex,
    activeIndicatorIndex,
    images.length,
    imagesPerSlide,
    setActiveIndicatorIndex,
    setActiveImageIndex,
    setVideoPlayingForSoftline,
  ]);

  const onPreviousHeadLine = useCallback(() => {
    setVideoPlaying(false);
    setVideoPlayingForSoftline(false);
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex - 1;

    const isFirstImage = activeImageIndex === 0;
    const isFirstImageOfSlide = activeImageIndex % imagesPerSlide === 0;

    if (isFirstImage) {
      newActiveImageIndex = images.length - 1;
      newActiveIndicatorIndex = Math.ceil(images.length / imagesPerSlide) - 1;
    } else if (!isFirstImage && isFirstImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex - 1;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [
    activeImageIndex,
    activeIndicatorIndex,
    images.length,
    imagesPerSlide,
    setActiveIndicatorIndex,
    setActiveImageIndex,
    setVideoPlayingForSoftline,
  ]);

  // Image processing
  const activeImage = images[activeImageIndex] || IMAGE_GALLERY.DEFAULT_IMAGE;
  const { mediaId: activeImageId, mediaUrl: activeImageUrl } = getMediaData(activeImage);
  const isScene7Default = activeImageId === IMAGE_GALLERY.DEFAULT_IMAGE;
  const variantId = activeImageId.includes('/') ? activeImageId.split('/')[1] : activeImageId;

  // Image URL generation
  const activeImgZoomBaseUrl = getImages({
    variantId,
    type: 'headline',
    attr: {
      width: headlineImageWidth,
      height: headlineImageHeight,
      quality: imageQuality,
    },
    isCFMediasEnabled,
    sellerName,
    regionCode,
    url: activeImageUrl,
    mediaDomains,
    fallBackImagesDomain,
  });

  const imageCrop = isScene7Default ? {} : crop;
  // Use original zoom factor like v1
  const zoomFactor = 1;
  const activeImgZoomBaseSrc = getImages({
    variantId,
    type: 'headline',
    attr: {
      width: Math.round(zoomSize.x * zoomFactor),
      height: Math.round(zoomSize.y * zoomFactor),
      quality: imageQuality,
      crop: imageCrop,
    },
    isCFMediasEnabled,
    sellerName,
    regionCode,
    url: activeImageUrl,
    mediaDomains,
    fallBackImagesDomain,
  });

  const isVideoCheck = isVideo(activeImage);
  const isAmbientCheck = isShopTheProjectEnabledWithAmbientImage(activeImageUrl);
  const shouldShowZoomValue = zoomLevel && !isVideoCheck && !isAmbientCheck;

  return {
    // State values
    zoomLevel,
    isVideoPlaying,
    isCoachmarkVisible,
    headlineRef,

    // Configuration values
    isCFMediasEnabled,
    mediaDomains,
    fallBackImagesDomain,
    regionCode,
    store,
    sellerName,

    // Processed data
    activeImage,
    activeImageId,
    activeImageUrl,
    isScene7Default,
    variantId,
    activeImgZoomBaseUrl,
    imageCrop,
    activeImgZoomBaseSrc,

    // Event handlers
    handleMouseLeave,
    handleMouseOver,
    handleMouseOverButton,
    onNextHeadline,
    onPreviousHeadLine,
    onErrorFallback,

    // Utility functions
    isShopTheProjectEnabledWithAmbientImage,

    // State setters
    setZoomLevel,
    setVideoPlaying,
    setIsCoachmarkVisible,

    // Computed values
    shouldShowZoom: shouldShowZoomValue,
    isVideoContent: isVideoCheck,
    isYoutubeVideo: isVideoCheck && activeImageUrl.includes('youtube'),
    isAmbientImage: isAmbientCheck,
    altText: `${brandName} - ${productName}`,
  };
};

// Export as named export to follow ESLint preference
// export default useImageGalleryHeadline;
